import { endOfDay, startOfDay, subDays } from 'date-fns'
import { useCallback, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import { useGetPackageLog } from '@/api/packages/get-package-log'
import { TableGroup } from '@/components/layout/table-group'
import { DatePickerInput } from '@/components/ui/date-picker-input'
import { RowAction } from '@/components/ui/table'
import { routerFactory } from '@/factories'
import { useFilters, useSearchParams } from '@/hooks'
import { DateRange, PackageRoomSession } from '@/models'
import { selectActiveBuilding } from '@/reducers/buildingSelectorReducer'

import {
  getPackageLogColumns,
  packageLogInitialFilters,
  packageLogQueryFiltersSchema,
} from './package-log.constants'

export const PackageLogPage = () => {
  const { t } = useTranslation(['packages'])
  const { navigate } = routerFactory.useRouter()
  const activeBuilding = useSelector(selectActiveBuilding)

  const handleShowDetails = useCallback(
    (session: PackageRoomSession) => {
      navigate({
        params: { sessionId: session.sessionId },
        path: '/dashboard/building-dashboard/packages/log/:sessionId',
      })
    },
    [navigate],
  )

  const columns = useMemo(
    () =>
      getPackageLogColumns({
        t,
        timezone: activeBuilding?.timezone,
      }),
    [activeBuilding?.timezone, handleShowDetails, t],
  )

  const rowActions: RowAction<PackageRoomSession>[] = useMemo(
    () => [
      {
        label: t('packages:log.details'),
        onClick: handleShowDetails,
        primary: true,
      },
    ],
    [handleShowDetails, t],
  )

  const searchParams = useSearchParams({
    initialState: packageLogInitialFilters,
    schema: packageLogQueryFiltersSchema,
  })

  const filters = useFilters({
    controlledState: searchParams.parsedQuery,
    initialState: packageLogInitialFilters,
    onFiltersChange: searchParams.writeParams,
  })

  const fromDateFilter = filters.getFilter('fromDate')
  const toDateFilter = filters.getFilter('toDate')
  const dateRange: DateRange = [
    fromDateFilter ? new Date(fromDateFilter) : null,
    toDateFilter ? new Date(toDateFilter) : null,
  ]

  const { data, isFetching, error } = useGetPackageLog({
    buildingId: activeBuilding?.id,
    endDate: toDateFilter,
    limit: filters.getFilter('limit'),
    skip: filters.getFilter('skip') ?? 0,
    startDate: fromDateFilter,
  })
  const { total = 0, data: sessions = [] } = data || {}

  const handleDateChange = (range: DateRange) => {
    filters.setFilters({
      fromDate: range[0]?.getTime(),
      toDate: range[1]?.getTime(),
    })
  }

  // Note: Add initial filters on component mount
  // Do it only if there are no initial filters passed from the URL
  useEffect(() => {
    if (searchParams.parsedQuery.fromDate || searchParams.parsedQuery.toDate) {
      return
    }

    filters.setFilters({
      fromDate: startOfDay(subDays(new Date(), 30)).getTime(),
      toDate: endOfDay(new Date()).getTime(),
    })
  }, [])

  return (
    <TableGroup
      id="package-log"
      columns={columns}
      data={sessions}
      error={error}
      getRowKey={(session) => session.sessionId}
      loading={isFetching}
      navigation={{
        datePicker: (
          <DatePickerInput
            mode="range"
            placeholder={t('packages:log.timeRangePlaceholder')}
            size="small"
            value={dateRange}
            maxDate={new Date()}
            onRangeChange={handleDateChange}
          />
        ),
      }}
      rowActions={rowActions}
      pagination={{
        paginationFilters: filters,
        total,
      }}
      tableFilters={filters}
    />
  )
}
